import React, { useState, useCallback } from 'react'
import {
  Box,
  Collapse,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Typography,
  Button,
  Stack,
  Paper,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { EVENT_TYPES_ARRAY, BOOKING_STATUSES, BOOKING_PAYMENT_STATUSES, STATUS_SESSION_BOOKED, STATUS_SESSION_CANCELLED } from 'src/consumer/constants'
import styled from 'styled-components'

const FiltersContainer = styled(Paper)`
  margin-bottom: 24px;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: #ffffff;
  
  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 16px;
  }
`

const FilterHeader = styled(Box).withConfig({
  shouldForwardProp: (prop) => prop !== 'expanded',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${(props) => (props.expanded ? '16px' : '0')};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
`

const ActiveFiltersChips = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`

const FilterGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const FilterActions = styled(Box)`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

export default function BookedEventsFilters({ filters, onFiltersChange, onReset, hideSessionTypeFilter = false }) {
  const [expanded, setExpanded] = useState(false)
  const [localFilters, setLocalFilters] = useState(filters || {})

  const handleFilterChange = useCallback(
    (key, value) => {
      const newFilters = { ...localFilters, [key]: value }
      setLocalFilters(newFilters)
      onFiltersChange(newFilters)
    },
    [localFilters, onFiltersChange],
  )

  const handleReset = useCallback(() => {
    const resetFilters = {}
    setLocalFilters(resetFilters)
    onFiltersChange(resetFilters)
    if (onReset) onReset()
  }, [onFiltersChange, onReset])

  const activeFiltersCount = Object.keys(localFilters).filter(
    (key) => localFilters[key] !== null && localFilters[key] !== undefined && localFilters[key] !== '',
  ).length

  const getActiveFilterLabel = (key, value) => {
    switch (key) {
      case 'startTime':
        return `From: ${dayjs(value).format('MMM DD, YYYY')}`
      case 'endTime':
        return `To: ${dayjs(value).format('MMM DD, YYYY')}`
      case 'eventTypes':
        return `Event Types: ${Array.isArray(value) ? value.length : 1} selected`
      case 'statusIds':
        return `Status: ${Array.isArray(value) ? value.join(', ') : value}`
      case 'paymentStatusIds':
        return `Payment: ${Array.isArray(value) ? value.join(', ') : value}`
      case 'showBlockedSession':
        return value ? 'Including Blocked Sessions' : null
      default:
        return `${key}: ${value}`
    }
  }

  const removeFilter = (key) => {
    const newFilters = { ...localFilters }
    delete newFilters[key]
    setLocalFilters(newFilters)
    onFiltersChange(newFilters)
  }

  return (
    <FiltersContainer>
      <FilterHeader expanded={expanded} onClick={() => setExpanded(!expanded)}>
        <Box display="flex" alignItems="center" gap={1}>
          <FilterListIcon color="primary" />
          <Typography variant="h6" component="div" fontWeight={600}>
            Filters
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip
              label={activeFiltersCount}
              size="small"
              color="primary"
              sx={{ minWidth: '24px', height: '24px' }}
            />
          )}
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </FilterHeader>

      {activeFiltersCount > 0 && !expanded && (
        <ActiveFiltersChips>
          {Object.entries(localFilters)
            .filter(([key, value]) => {
              if (key === 'showBlockedSession') return value === true
              return value !== null && value !== undefined && value !== ''
            })
            .map(([key, value]) => {
              const label = getActiveFilterLabel(key, value)
              if (!label) return null
              return (
                <Chip
                  key={key}
                  label={label}
                  onDelete={() => removeFilter(key)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )
            })}
        </ActiveFiltersChips>
      )}

      <Collapse in={expanded}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FilterGrid>
            <DatePicker
              label="Start Date"
              value={localFilters.startTime ? dayjs(localFilters.startTime) : null}
              onChange={(newValue) => {
                handleFilterChange('startTime', newValue ? newValue.toISOString() : null)
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />

            <DatePicker
              label="End Date"
              value={localFilters.endTime ? dayjs(localFilters.endTime) : null}
              onChange={(newValue) => {
                handleFilterChange('endTime', newValue ? newValue.toISOString() : null)
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Event Types</InputLabel>
              <Select
                multiple
                value={localFilters.eventTypes || []}
                onChange={(e) => handleFilterChange('eventTypes', e.target.value)}
                renderValue={(selected) => {
                  if (selected.length === 0) return 'All Event Types'
                  if (selected.length === 1) {
                    const eventType = EVENT_TYPES_ARRAY.find((et) => et.id === selected[0])
                    return eventType?.title || selected[0]
                  }
                  return `${selected.length} selected`
                }}
              >
                {EVENT_TYPES_ARRAY.map((eventType) => (
                  <MenuItem key={eventType.id} value={eventType.id}>
                    {eventType.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={localFilters.statusIds || []}
                onChange={(e) => handleFilterChange('statusIds', e.target.value)}
                renderValue={(selected) => {
                  if (selected.length === 0) return 'All Statuses'
                  return selected.join(', ')
                }}
              >
                <MenuItem value="booked">Booked</MenuItem>
                <MenuItem value="booking-initiated">Booking Initiated</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                multiple
                value={localFilters.paymentStatusIds || []}
                onChange={(e) => handleFilterChange('paymentStatusIds', e.target.value)}
                renderValue={(selected) => {
                  if (selected.length === 0) return 'All Payment Statuses'
                  return selected.join(', ')
                }}
              >
                {Object.entries(BOOKING_PAYMENT_STATUSES).map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!hideSessionTypeFilter && (
              <FormControl fullWidth size="small">
                <InputLabel>Session Type</InputLabel>
                <Select
                  value={localFilters.showBlockedSession ? 'true' : 'false'}
                  onChange={(e) => handleFilterChange('showBlockedSession', e.target.value === 'true')}
                  label="Session Type"
                >
                  <MenuItem value="false">Paid & Booked Only</MenuItem>
                  <MenuItem value="true">Include Blocked Sessions</MenuItem>
                </Select>
              </FormControl>
            )}
          </FilterGrid>

          <FilterActions>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleReset}
              size="small"
            >
              Clear All
            </Button>
          </FilterActions>
        </LocalizationProvider>
      </Collapse>
    </FiltersContainer>
  )
}

